<template>
  <span ref="wrapperRef" class="inline-flex">
    <slot :toggle="toggle" :active="showPicker" />
    <Teleport to="body">
      <template v-if="showPicker">
        <div class="fixed inset-0 z-(--z-popover)" @click="close"></div>
        <div ref="pickerEl" class="fixed z-[calc(var(--z-popover)+1)]" :style="pickerStyle">
          <Picker
            autoFocus
            :data="getEmojiIndex()"
            set="apple"
            @select="onSelect"
          />
        </div>
      </template>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, computed, nextTick, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { Picker } from 'emoji-mart-vue-fast/src';
import 'emoji-mart-vue-fast/css/emoji-mart.css';
import { getEmojiIndex } from '@/utils/emojiIndex';

const props = defineProps({
  comment: Object,
  taskId: String,
  placement: { type: String, default: 'up' }, // 'up' opens above, 'down' opens below
});

const emit = defineEmits(['openChange']);

const store = useStore();
const showPicker = ref(false);
const ready = ref(false);
const wrapperRef = ref(null);
const pickerEl = ref(null);
const position = ref({ top: 0, left: 0 });

const pickerStyle = computed(() => ({
  top: `${position.value.top}px`,
  left: `${position.value.left}px`,
  visibility: ready.value ? 'visible' : 'hidden',
}));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// fallback size used before emoji-mart has laid out, so the picker is always anchored to the trigger
const ESTIMATED_WIDTH = 340;
const ESTIMATED_HEIGHT = 420;

const updatePosition = () => {
  if (!wrapperRef.value) return;
  const gap = 6;
  const pad = 8;
  const wr = wrapperRef.value.getBoundingClientRect();
  const pr = pickerEl.value && pickerEl.value.getBoundingClientRect();
  const w = pr && pr.width ? pr.width : ESTIMATED_WIDTH;
  const h = pr && pr.height ? pr.height : ESTIMATED_HEIGHT;

  // right edge of the picker lines up with the right edge of the trigger
  const left = wr.right - w;
  let top;
  if (props.placement === 'down') {
    top = wr.bottom + gap;
    if (top + h > window.innerHeight - pad) top = wr.top - h - gap;
  } else {
    top = wr.top - h - gap;
    if (top < pad) top = wr.bottom + gap;
  }

  position.value = {
    left: clamp(left, pad, Math.max(pad, window.innerWidth - w - pad)),
    top: clamp(top, pad, Math.max(pad, window.innerHeight - h - pad)),
  };
};

const open = async () => {
  showPicker.value = true;
  ready.value = false;
  emit('openChange', true);
  await nextTick();
  if (!showPicker.value) return;
  updatePosition();   // anchor immediately (estimate if not measured yet)
  ready.value = true;
  // refine once emoji-mart has rendered its real size
  requestAnimationFrame(() => { if (showPicker.value) updatePosition(); });
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition, true);
};

const close = () => {
  if (!showPicker.value) return;
  showPicker.value = false;
  ready.value = false;
  emit('openChange', false);
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition, true);
};

const toggle = () => {
  if (showPicker.value) close();
  else open();
};

const onSelect = (event) => {
  close();
  store.dispatch('project/actionReactToComment', {
    commentId: props.comment.id,
    taskId: props.taskId,
    emoji: event.native,
  });
};

onUnmounted(close);
</script>
